import React, { useState, useEffect } from "react";
import { BsDownload } from "react-icons/bs";
import { toast } from "react-toastify";
import Loader from "../components/Loader";
import { PaginatedItems } from "../components/Paginated";
import { getScanningPdReport } from "../Path/Path";

// SCANNING PD FORMAT — per carton / per article (EAN) breakdown of a PO's
// receiving, sourced from the packingList collection that the mobile GRN
// module writes to. Short/Excess = received - packed (negative = short).
const HEADERS = [
  "PO NO",
  "CTN NO",
  "ARTICLE NO",
  "ARTICLE DESCRIPTION",
  "EAN NO",
  "PACKED QTY",
  "RECEIVED QTY",
  "SHORT QTY/ EXCESS QTY",
];
const COL_NAMES = [
  "poNumber",
  "cartonNo",
  "articleNo",
  "articleDesc",
  "EAN",
  "packedQty",
  "receivingProcessedQuantity",
  "shortExcess",
];

const shortExcessValue = (item) => {
  const packed = Number(item?.packedQty) || 0;
  const received = Number(item?.receivingProcessedQuantity) || 0;
  return received - packed;
};

const renderShortExcess = (val) => {
  let color = "#ddd";
  let label = "0";
  if (val < 0) {
    color = "#ff6b6b";
    label = `${val} (Short)`;
  } else if (val > 0) {
    color = "#51cf66";
    label = `+${val} (Excess)`;
  }
  return <span style={{ color, fontWeight: 600 }}>{label}</span>;
};

const ScanningPdReport = () => {
  const [poNumber, setPoNumber] = useState("");
  const [cartonNo, setCartonNo] = useState("");
  const [rows, setRows] = useState([]);
  const [pageInfo, setPageInfo] = useState({});
  const [paginationData, setPaginationData] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch a page of the list. poNumber / cartonNo (if entered) narrow the result.
  const getReport = async (pageNumber = 1) => {
    setLoading(true);
    try {
      const res = await getScanningPdReport({
        poNumber: poNumber.trim() || undefined,
        cartonNo: cartonNo.trim() || undefined,
        page: pageNumber,
      });
      const list = res?.data?.data;
      if (Array.isArray(list)) {
        setRows(list);
        setPageInfo(res?.data || {});
        if (list.length === 0) toast.error("No data found.");
      } else {
        console.error("Unexpected API response structure:", res);
        setRows([]);
      }
    } catch (err) {
      console.error("Error fetching Scanning PD report:", err);
      toast.error("Failed to fetch report.");
    } finally {
      setLoading(false);
    }
  };

  // Pull every matching row (size=all) for the current filter and download a CSV.
  const exportCsv = async () => {
    setLoading(true);
    try {
      const res = await getScanningPdReport({
        poNumber: poNumber.trim() || undefined,
        cartonNo: cartonNo.trim() || undefined,
        size: "all",
      });
      const list = res?.data?.data || [];
      if (list.length === 0) {
        toast.error("Nothing to export.");
        return;
      }
      const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
      // leading tab keeps Excel from stripping leading zeros on long codes
      const csvRows = list.map((item) =>
        [
          escape(item?.poNumber || ""),
          escape(item?.cartonNo || ""),
          escape(item?.articleNo ? `\t${item.articleNo}` : ""),
          escape(item?.articleDesc || ""),
          escape(item?.EAN ? `\t${item.EAN}` : ""),
          Number(item?.packedQty) || 0,
          Number(item?.receivingProcessedQuantity) || 0,
          shortExcessValue(item),
        ].join(",")
      );
      const csv = [HEADERS.map(escape).join(","), ...csvRows].join("\n");
      const url = window.URL.createObjectURL(
        new Blob([csv], { type: "text/csv;charset=utf-8;" })
      );
      const link = document.createElement("a");
      link.href = url;
      const namePart =
        [poNumber.trim(), cartonNo.trim()].filter(Boolean).join("_") || "all";
      link.setAttribute("download", `PO_Scanning_Report_${namePart}.csv`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (err) {
      console.error("Error exporting Scanning PD report:", err);
      toast.error("Failed to export CSV.");
    } finally {
      setLoading(false);
    }
  };

  const tableData = rows.map((item) => ({
    poNumber: item?.poNumber || "--",
    cartonNo: item?.cartonNo || "--",
    articleNo: (
      <span>
        {item?.articleNo || "--"}
        {item?.state === "noExtra" && (
          <span
            style={{
              marginLeft: "6px",
              fontSize: "10px",
              color: "#000",
              background: "#ffd43b",
              borderRadius: "3px",
              padding: "1px 5px",
            }}
          >
            UNEXPECTED
          </span>
        )}
        {item?.state === "extra" && (
          <span
            style={{
              marginLeft: "6px",
              fontSize: "10px",
              color: "#fff",
              background: "#fd7e14",
              borderRadius: "3px",
              padding: "1px 5px",
            }}
          >
            WRONG CTN
          </span>
        )}
      </span>
    ),
    articleDesc: item?.articleDesc || "--",
    EAN: item?.EAN || "--",
    packedQty: Number(item?.packedQty) || 0,
    receivingProcessedQuantity: Number(item?.receivingProcessedQuantity) || 0,
    shortExcess: renderShortExcess(shortExcessValue(item)),
  }));

  return (
    <React.Fragment>
      <Loader loading={loading} />
      <div className="dashboard-main">
        <div
          className="dashboard-top"
          style={{ display: "flex", flexDirection: "column", padding: "10px" }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <p className="stock-count" style={{ fontSize: "25px", margin: 0 }}>
              PO Scanning Report
            </p>
            <div
              onClick={exportCsv}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                cursor: "pointer",
              }}
            >
              <p style={{ color: "black", fontSize: "16px", margin: 0 }}>
                Export CSV
              </p>
              <BsDownload color="black" size={16} />
            </div>
          </div>
          <div>
            <h1 style={{ color: "black", fontSize: "15px", marginLeft: "5px" }}>
              {new Date().toLocaleString("hi-IN")}
            </h1>
          </div>
        </div>

        <div className="dashboard-bottom" style={{ marginTop: "0.5%" }}>
          <div
            style={{
              display: "flex",
              gap: "10px",
              alignItems: "center",
              flexWrap: "wrap",
              marginBottom: "10px",
            }}
          >
            <input
              type="text"
              placeholder="Filter by PO Number"
              value={poNumber}
              onChange={(e) => setPoNumber(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && getReport(1)}
              style={{
                padding: "8px 12px",
                borderRadius: "4px",
                border: "1px solid #555",
                background: "#222",
                color: "#fff",
                width: "260px",
              }}
            />
            <input
              type="text"
              placeholder="Filter by Carton No"
              value={cartonNo}
              onChange={(e) => setCartonNo(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && getReport(1)}
              style={{
                padding: "8px 12px",
                borderRadius: "4px",
                border: "1px solid #555",
                background: "#222",
                color: "#fff",
                width: "260px",
              }}
            />
            <button onClick={() => getReport(1)} className="run-btn">
              <p className="run-text">Search</p>
            </button>
            {poNumber || cartonNo ? (
              <button
                onClick={() => {
                  setPoNumber("");
                  setCartonNo("");
                  setTimeout(() => getReport(1), 0);
                }}
                className="view-btn"
              >
                <p className="run-text">Clear</p>
              </button>
            ) : null}
          </div>

          <div style={{ marginTop: "10px", overflowX: "auto", height: "600px" }}>
            <PaginatedItems
              pageCountNew={
                (pageInfo?.next || 0) + (pageInfo?.previous || 0) + 1
              }
              getData={(pageNumber) => getReport(pageNumber)}
              totalLength={paginationData?.next}
              itemsPerPage={10}
              headers={HEADERS}
              colNames={COL_NAMES}
              forcePage={paginationData?.previous || 0}
              data={tableData}
            />
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: "10px",
              color: "white",
              padding: "10px",
            }}
          >
            <h5>ROWS PER PAGE: 10</h5>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default ScanningPdReport;
